import React from 'react';
import BlockingSpinnerOverlay from 'components/BlockingSpinnerOverlay';
import TermsField from 'components/fields/TermsField';
import TextField from 'components/fields/TextField';
import {
  getCustomBrandingDetail,
  isMobileNumberRequiredForLinking,
} from 'helpers/customBranding';
import { WORKFLOW_TYPE_LINK_ACCOUNT } from 'helpers/registrationWorkflow';
import PropTypes from 'prop-types';
import { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Button, Col, Form, Row } from 'reactstrap';
import { reduxForm } from 'redux-form';
import identityClient from '../../../clients/identityClient';
import {
  isPhoneOnExistingAccount,
  shouldBlockLinkingPhoneBeforeVerify,
} from '../../../helpers/accountMatchingUtils';
import PasswordField from '../../../components/fields/PasswordField';
import PhoneNumberField, {
  normalizePhone,
} from '../../../components/fields/PhoneNumberField';
import HpaStoreContext from '../../../contexts/HpaStoreContext';
import errorMessages, { toErrorAlertState } from '../../../helpers/errorMessages';
import { error as err, registrationSteps } from '../PreSignUp';
import ErrorAlert from '../PreSignUp/ErrorAlert';

const Registration = ({
  isRequestPending,
  PasswordComplexityFeature,
  navigate,
  handleSubmit,
  registrationFields,
  invalid,
  workflowType,
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(err);
  const [isMobileFieldVisible, setIsMobileFieldVisible] = useState(false);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const isBlockingOverlay =
    (isLoading || isRequestPending) && !isConfigLoading;
  const isLoadingOrPending = isLoading || isRequestPending || isConfigLoading;
  const isPasswordInvalid = PasswordComplexityFeature && !isPasswordValid;
  const isContinueDisabled = isLoadingOrPending || isPasswordInvalid || invalid;
  const { email } = registrationFields || {};
  const params = new URLSearchParams(window.location.search);
  const queryString = params.toString();
  const history = useHistory();
  const context = useContext(HpaStoreContext);

  const RegistrationInstructionText = getCustomBrandingDetail(
    context,
    'registrationInstructionText',
  );

  useEffect(() => {
    setIsConfigLoading(true);
    try {
      const fromState = registrationFields?.isMobileRequired === true;
      const fromConfig = isMobileNumberRequiredForLinking(context, params);
      setIsMobileFieldVisible(fromState || fromConfig);
    } catch (e) {
      console.warn('Error determining mobile requirement', e);
      setIsMobileFieldVisible(registrationFields?.isMobileRequired === true);
    } finally {
      setIsConfigLoading(false);
    }
  }, [
    context?.config,
    context?.customBranding,
    params,
    registrationFields?.isMobileRequired,
  ]);

  const onPasswordToggle = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const onContinue = async (values) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    const { password, firstName, lastName, phone } = values;
    try {
      if (isMobileFieldVisible && phone) {
        const normalizedPhone = normalizePhone(phone);
        const provisionedHstreamId =
          registrationFields?.hStreamId ||
          registrationFields?.hstreamId ||
          null;
        const normalizedProvisionedId = provisionedHstreamId
          ? String(provisionedHstreamId).trim()
          : '';
        const accountLookup = await identityClient.getAccountByPhoneNumber(
          normalizedPhone,
        );
        const lookupHstreamId =
          accountLookup?.exists && accountLookup?.hstreamId
            ? String(accountLookup.hstreamId).trim()
            : '';

        if (
          shouldBlockLinkingPhoneBeforeVerify(accountLookup, {
            isProvisioned: registrationFields?.isProvisioned === true,
            isConfirmed: registrationFields?.isConfirmed === true,
            workflowType,
          })
        ) {
          setError({
            errorFound: true,
            errorMessage: errorMessages.phoneOnAnotherAccountLinking.message,
            errorType: errorMessages.phoneOnAnotherAccountLinking.type,
          });
          return;
        }

        const hStreamIdForOtp = normalizedProvisionedId || null;
        const matchedHstreamId = isPhoneOnExistingAccount(accountLookup)
          ? lookupHstreamId
          : undefined;

        await identityClient.checkPasswordReuse(
          matchedHstreamId || hStreamIdForOtp || lookupHstreamId,
          password,
          email,
        );

        await identityClient.sendRegistrationOTP(
          '',
          hStreamIdForOtp,
          normalizedPhone,
          workflowType,
        );
        navigate(registrationSteps.VERIFICATIONCODE, {
          email,
          firstName,
          lastName,
          password,
          phone: normalizedPhone,
          hStreamId: hStreamIdForOtp,
          matchedHstreamId,
          isConfirmed: registrationFields?.isConfirmed === true,
          workflowType,
          isMobileRequired: true,
          isProvisioned: registrationFields?.isProvisioned === true,
          provisionedHStreamId: provisionedHstreamId || '',
        });
        return;
      }

      const response = await identityClient.registerUser(
        firstName,
        lastName,
        email,
        password,
        phone,
        workflowType,
      );
      const hstreamId = response.accountId;

      if (workflowType === WORKFLOW_TYPE_LINK_ACCOUNT) {
        try {
          const linkUserResponse = await identityClient.linkUser(
            hstreamId,
            email,
            queryString,
          );
          navigate(registrationSteps.SUCCESS, { code: linkUserResponse?.code });
          return;
        } catch (linkError) {
          history.push({ pathname: '/account/linkError', search: queryString });
          return;
        }
      }
      navigate(registrationSteps.SUCCESS);
    } catch (e) {
      setError(
        e.response?.data?.status === 400 || e.response?.data?.status === 409
          ? toErrorAlertState(e.response?.data?.detail)
          : {
              errorFound: true,
              errorMessage: errorMessages?.processing?.message,
            },
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {RegistrationInstructionText &&
        RegistrationInstructionText.value &&
        RegistrationInstructionText.value.trim() !== '' && (
          <div className="mt-4">
            <Alert color="info">{RegistrationInstructionText?.value}</Alert>
          </div>
        )}
      <Form
        className="mt-4 position-relative"
        onSubmit={handleSubmit((values) => {
          if (isLoading) {
            return;
          }
          return onContinue(values);
        })}
        aria-busy={isBlockingOverlay}
      >
        <BlockingSpinnerOverlay
          show={isBlockingOverlay}
          ariaLabel="Loading..."
        />
        {error.errorFound && <ErrorAlert error={error} />}
        <TextField label="Email" name="email" disabled />
        <Row>
          <Col sm="6">
            <TextField
              label="First Name"
              name="firstName"
              disabled={isRequestPending}
              required
            />
          </Col>
          <Col sm="6">
            <TextField
              label="Last Name"
              name="lastName"
              disabled={isRequestPending}
              required
            />
          </Col>
        </Row>
        {isMobileFieldVisible && (
          <>
            <PhoneNumberField
              label="Mobile Number"
              name="phone"
              id="registrationPhone"
              disabled={isRequestPending}
              required
            />
            <div
              className="form-text text-muted mb-3"
              style={{ fontSize: '0.875rem', marginTop: '-0.5rem' }}
            >
              Your phone number will be used for password recovery and will not be
              used for any other purposes without your consent.
            </div>
          </>
        )}
        <PasswordField
          displayPassword={isPasswordVisible}
          onToggle={onPasswordToggle}
          disabled={isRequestPending}
          featureToggle={!PasswordComplexityFeature}
          showTooltip={true}
          setIsPasswordValid={setIsPasswordValid}
        />

        <PasswordField
          confirm
          displayPassword={isPasswordVisible}
          onToggle={onPasswordToggle}
          disabled={isRequestPending}
          featureToggle={!PasswordComplexityFeature}
          showTooltip={false}
        />

        <TermsField disabled={isRequestPending} />

        <Row className="justify-content-center">
          <Button
            id="registration"
            color="primary"
            className="btn btn-md"
            type="submit"
            style={{ width: '25%' }}
            disabled={isContinueDisabled}
          >
            Continue
          </Button>
        </Row>
      </Form>
    </>
  );
};

Registration.propTypes = {
  isRequestPending: PropTypes.bool,
  PasswordComplexityFeature: PropTypes.bool,
  navigate: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  registrationFields: PropTypes.shape({
    email: PropTypes.string,
  }),
  invalid: PropTypes.bool,
  workflowType: PropTypes.string,
};

Registration.defaultProps = {
  isRequestPending: false,
  PasswordComplexityFeature: false,
  registrationFields: {},
  invalid: false,
  workflowType: '',
};

export default reduxForm({
  form: 'newRegistrationForm',
})(Registration);
